import { createFileRoute } from '@tanstack/react-router';
import { useTheme } from '../../../contexts/ThemeContext';

/**
 * Creators Publish - Plugin publishing and CI/CD management
 */
export const Route = createFileRoute('/_authenticated/creators/publish')({
  component: CreatorsPublish,
});

function CreatorsPublish() {
  const { theme } = useTheme();

  const colors = {
    dark: {
      text: '#ffffff',
      textMuted: '#9ca3af',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      buttonBg: '#667eea',
      statusBg: 'rgba(102, 126, 234, 0.1)',
      buttonInactive: 'rgba(102, 126, 234, 0.2)',
      buttonInactiveText: '#a8b2ff'
    },
    light: {
      text: '#111827',
      textMuted: '#6b7280',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      buttonBg: '#2563eb',
      statusBg: '#f9fafb',
      buttonInactive: '#f3f4f6',
      buttonInactiveText: '#374151'
    }
  };

  const c = colors[theme];

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: c.text,
            marginBottom: '0.5rem'
          }}>
            Publish & CI/CD
          </h1>
          <p style={{ color: c.textMuted, fontSize: '1rem' }}>
            Manage plugin builds, testing, and deployment pipeline
          </p>
        </div>

        {/* Build Status */}
        <div style={{
          background: c.cardBg,
          border: `1px solid ${c.cardBorder}`,
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '2rem',
          backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
          boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{
            fontSize: '1.25rem',
            fontWeight: '600',
            color: c.text,
            marginBottom: '1rem'
          }}>
            Build Status
          </h2>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1rem',
            backgroundColor: c.statusBg,
            borderRadius: '0.375rem'
          }}>
            <span style={{
              width: '0.75rem',
              height: '0.75rem',
              borderRadius: '50%',
              backgroundColor: '#10b981'
            }}></span>
            <span style={{ color: c.textMuted, fontSize: '0.875rem' }}>
              No active builds
            </span>
          </div>
        </div>

        {/* Recent Deployments */}
        <div style={{
          background: c.cardBg,
          border: `1px solid ${c.cardBorder}`,
          borderRadius: '0.5rem',
          padding: '1.5rem',
          marginBottom: '2rem',
          backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
          boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h2 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: c.text
            }}>
              Recent Deployments
            </h2>
            <button style={{
              backgroundColor: c.buttonBg,
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              New Deployment
            </button>
          </div>
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            color: c.textMuted
          }}>
            No deployments yet
          </div>
        </div>

        {/* CI/CD Configuration */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '1.5rem'
        }}>
          <ConfigCard
            title="Build Configuration"
            description="Configure build settings for VST3, AU, and AAX formats"
            status="Not configured"
            actionText="Configure"
            theme={theme}
          />
          <ConfigCard
            title="Testing Pipeline"
            description="Set up automated testing for your plugins"
            status="Not configured"
            actionText="Set Up"
            theme={theme}
          />
          <ConfigCard
            title="Deployment Settings"
            description="Configure automatic deployment to marketplace"
            status="Not configured"
            actionText="Configure"
            theme={theme}
          />
        </div>
      </div>
    </div>
  );
}

interface ConfigCardProps {
  title: string;
  description: string;
  status: string;
  actionText: string;
  theme: 'dark' | 'light';
}

function ConfigCard({ title, description, status, actionText, theme }: ConfigCardProps) {
  const colors = {
    dark: {
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      buttonBg: 'rgba(102, 126, 234, 0.2)',
      buttonText: '#a8b2ff'
    },
    light: {
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      buttonBg: '#f3f4f6',
      buttonText: '#374151'
    }
  };

  const c = colors[theme];

  return (
    <div style={{
      background: c.cardBg,
      border: `1px solid ${c.cardBorder}`,
      borderRadius: '0.5rem',
      padding: '1.5rem',
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
      boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{
        fontSize: '1.125rem',
        fontWeight: '600',
        color: c.text,
        marginBottom: '0.5rem'
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '0.875rem',
        color: c.textMuted,
        marginBottom: '1rem'
      }}>
        {description}
      </p>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{
          fontSize: '0.75rem',
          color: c.textMuted,
          fontWeight: '500',
          opacity: 0.7
        }}>
          {status}
        </span>
        <button style={{
          padding: '0.5rem 1rem',
          backgroundColor: c.buttonBg,
          border: 'none',
          borderRadius: '0.375rem',
          cursor: 'pointer',
          fontSize: '0.875rem',
          color: c.buttonText,
          fontWeight: '500'
        }}>
          {actionText}
        </button>
      </div>
    </div>
  );
}

export default CreatorsPublish;
